/* eslint-disable no-plusplus */
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { Editor, Element, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { SlatePlugin } from '../../../../../slatePlugins/slatePlugins.types';
import {
  ELEMENT_OUTLINE_PARAGRAPH,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA_BULLET_LIST,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER_TITLE,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA_LIST_ITEM,
  ELEMENT_OUTLINE_PARAGRAPH_IDEA_TITLE,
  ELEMENT_OUTLINE_PARAGRAPH_TITLE,
} from './OutlineParagraphPlugin.types';
import { outlineParagraphTransforms } from './transforms';

const OutlineParagraph = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraph').then((mod) => mod.OutlineParagraph),
));
const OutlineParagraphIdea = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdea').then((mod) => mod.OutlineParagraphIdea),
));
const OutlineParagraphIdeaBulletList = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdeaBulletList').then(
    (mod) => mod.OutlineParagraphIdeaBulletList,
  ),
));
const OutlineParagraphIdeaContainer = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdeaContainer').then(
    (mod) => mod.OutlineParagraphIdeaContainer,
  ),
));
const OutlineParagraphIdeaContainerTitle = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdeaContainerTitle').then(
    (mod) => mod.OutlineParagraphIdeaContainerTitle,
  ),
));
const OutlineParagraphIdeaListItem = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdeaListItem').then(
    (mod) => mod.OutlineParagraphIdeaListItem,
  ),
));
const OutlineParagraphIdeaTitle = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphIdeaTitle').then((mod) => mod.OutlineParagraphIdeaTitle),
));
const OutlineParagraphTitle = withDynamicBlogEditorPluginHydration()(dynamicEditorComponent(() =>
  import('./components/OutlineParagraphTitle').then((mod) => mod.OutlineParagraphTitle),
));

interface OutlineParagraphPluginOptions {}

export const OutlineParagraphPlugin = (
  options: OutlineParagraphPluginOptions = {},
): SlatePlugin => {
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;

      switch (element?.type) {
        case ELEMENT_OUTLINE_PARAGRAPH: {
          return <OutlineParagraph {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA: {
          return <OutlineParagraphIdea {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA_BULLET_LIST: {
          return <OutlineParagraphIdeaBulletList {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER: {
          return <OutlineParagraphIdeaContainer {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER_TITLE: {
          return <OutlineParagraphIdeaContainerTitle {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA_LIST_ITEM: {
          return <OutlineParagraphIdeaListItem {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_IDEA_TITLE: {
          return <OutlineParagraphIdeaTitle {...renderProps} {...options} />;
        }
        case ELEMENT_OUTLINE_PARAGRAPH_TITLE: {
          return <OutlineParagraphTitle {...renderProps} {...options} />;
        }
      }
      return undefined;
    },
  };
};

export const withSingleOutlineParagraphNode =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { insertBreak, normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      if (Element.isElement(node)) {
        switch (node?.type) {
          case ELEMENT_OUTLINE_PARAGRAPH: {
            // re-define shape

            const firstNode = node.children[0];
            const firstNodePath = [...path, 0];
            if (firstNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_TITLE) {
              Transforms.insertNodes(
                editor,
                outlineParagraphTransforms.createOutlineParagraphTitleNode({
                  text: 'Body Paragraph:',
                }),
                { at: firstNodePath },
              );
              return;
            }

            const secondNode = node.children[1];
            const secondNodePath = [...path, 1];

            if (secondNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER_TITLE) {
              Transforms.insertNodes(
                editor,
                outlineParagraphTransforms.createOutlineParagraphContainerTitleNode(),
                { at: secondNodePath },
              );
              return;
            }

            const thirdNode = node.children[2];
            const thirdNodePath = [...path, 2];

            if (thirdNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER) {
              if (thirdNode) {
                Transforms.removeNodes(editor, { at: thirdNodePath });
              }
              Transforms.insertNodes(
                editor,
                {
                  id: nanoid(),
                  type: ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER,
                  children: [
                    outlineParagraphTransforms.createOutlineParagraphIdeaNode(),
                    outlineParagraphTransforms.createOutlineParagraphIdeaNode(),
                  ],
                },
                { at: thirdNodePath },
              );
              return;
            }

            if (node.children.length > 3) {
              Transforms.removeNodes(editor, { at: [...path, 3] });
              return;
            }
            break;
          }
          case ELEMENT_OUTLINE_PARAGRAPH_IDEA: {
            const firstNode = node.children[0];
            const firstNodePath = [...path, 0];
            if (firstNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_IDEA_TITLE) {
              if (firstNode) {
                Transforms.removeNodes(editor, { at: firstNodePath });
              }
              Transforms.insertNodes(
                editor,
                outlineParagraphTransforms.createOutlineParagraphIdeaTitleNode(),
                { at: firstNodePath },
              );
              return;
            }

            const secondNode = node.children[1];
            const seconNodePath = [...path, 1];
            if (secondNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_IDEA_BULLET_LIST) {
              if (secondNode) {
                Transforms.removeNodes(editor, { at: seconNodePath });
              }
              Transforms.insertNodes(
                editor,
                outlineParagraphTransforms.createOutlineParagraphIdeaBulletListNode(),
                { at: seconNodePath },
              );
              return;
            }
            break;
          }
          case ELEMENT_OUTLINE_PARAGRAPH_IDEA_BULLET_LIST: {
            for (let i = 0; i < node.children.length; i++) {
              const childrenNode = node.children[i];
              if (childrenNode?.type !== ELEMENT_OUTLINE_PARAGRAPH_IDEA_LIST_ITEM) {
                Transforms.removeNodes(editor, { at: [...path, i] });
                return;
              }
            }
            if (node.children.length === 0) {
              Transforms.insertNodes(
                editor,
                outlineParagraphTransforms.createOutlineParagraphIdeaListItemNode(),
                { at: [...path, 0] },
              );
              return;
            }
            break;
          }
          default:
        }
      }
      normalizeNode(entry);
    };

    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [foundNode] = Editor.nodes(editor, {
          at: editor.selection,
          match: (n: any) =>
            [
              ELEMENT_OUTLINE_PARAGRAPH_IDEA_TITLE,
              ELEMENT_OUTLINE_PARAGRAPH_TITLE,
              ELEMENT_OUTLINE_PARAGRAPH_IDEA_CONTAINER_TITLE,
            ].includes(n?.type as string),
        });

        if (foundNode) {
          editor.insertText('\n');
          return;
        }
      }
      insertBreak();
    };

    return editor;
  };
